import React, { FC, useReducer, Fragment, Reducer, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { makeStyles, Theme } from '@material-ui/core/styles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Switch from '@material-ui/core/Switch';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import InfoIcon from '@material-ui/icons/Info';
import { useTranslation } from 'react-i18next';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import CustomEnrichmentActivity from './CustomEnrichmentActivity';
import {
  KeySettingDataInput,
  Classroom,
  KeyTeacherCreatedActivitySettings,
  KeyTeacherCreatedActivityInput,
  KeyTeacherCreatedActivity
} from 'types/schema-generated-types';
import GET_KEYBOARDING_CLASSROOM_SETTINGS from 'graph/settings/key/get-keyboarding-classroom-settings-query';
import { updateCache } from 'graph/update-cache';
import UPDATE_KEY_CLASSROOM_ENRICHMENT_SETTING_MUTATION from 'graph/settings/key/update-key-classroom-enrichment-setting-mutation';
import * as Yup from 'yup';
import useForm from 'hooks/use-lwt-form';
import keyBy from 'lodash/keyBy';
import useStore from 'store';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';
import startOfDay from 'date-fns/startOfDay';
import { isString } from 'lodash';

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(-1.3),
      '&.Mui-focused': {
        color: `${theme.palette.common.black} !important`
      }
    },
    activityHolder: {
      display: 'flex',
      flexDirection: 'column'
    }
  };
});

interface EnrichmentProps {
  classroom: Classroom;
  expanded: string | null;
  myChoiceKeyboarding: KeySettingDataInput | null;
  hearItTypeIt: KeySettingDataInput | null;
  teacherCreated: KeyTeacherCreatedActivitySettings;
  handleChangeExpanded: (panel: string | null) => () => void;
}

export interface EnrichmentState {
  // activities: Array<KeyTeacherCreatedActivityInput>;
  myChoiceKeyboarding: boolean;
  hearItTypeIt: boolean;
  teacherCreated: boolean;
}

type Action =
  | { type: 'SET_ENABLE_MY_CHOICE_KEYBOARDING'; payload: boolean }
  | { type: 'SET_ENABLE_HEAR_IT_TYPE_IT'; payload: boolean }
  | { type: 'SET_ENABLE_CUSTOM_ACTIVITY'; payload: boolean }
  | { type: 'SET_CUSTOM_ACTIVITIES'; payload: Array<KeyTeacherCreatedActivityInput> }
  | { type: 'REFRESH_STATE'; payload: EnrichmentState };

const enrichmentReducer = (state: EnrichmentState, action: Action): EnrichmentState => {
  switch (action.type) {
    case 'SET_ENABLE_MY_CHOICE_KEYBOARDING':
      return { ...state, myChoiceKeyboarding: action.payload };
    case 'SET_ENABLE_HEAR_IT_TYPE_IT':
      return { ...state, hearItTypeIt: action.payload };
    case 'SET_ENABLE_CUSTOM_ACTIVITY':
      return { ...state, teacherCreated: action.payload };
    case 'REFRESH_STATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const getClassroomKeySettingsMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_KEYBOARDING_CLASSROOM_SETTINGS,
      variables: {
        where: {
          classroomId
        }
      },
      reducer: (
        cachedData,
        { updateKeyClassroomSettingEnrichmentActivities }
      ): { keyClassroomSettings: any } => {
        const enrichmentActivitySettings = updateKeyClassroomSettingEnrichmentActivities;
        return {
          keyClassroomSettings: {
            ...cachedData?.keyClassroomSettings,
            enrichmentActivitySettings
          }
        };
      }
    }
  ])
});

const buildActivities = (aMap: {
  [key: string]: KeyTeacherCreatedActivityInput;
}): KeyTeacherCreatedActivityInput[] => {
  const result = Object.entries(aMap).map(([, value]: [string, KeyTeacherCreatedActivityInput]) => {
    return {
      id: value.id ? Number(value.id) : undefined, // ...(!id.includes('custom:') && { id: Number(id) }),
      title: value.title,
      date: isString(value.date) ? startOfDay(new Date(value.date)) : startOfDay(value.date),
      text: value.text
    };
  });

  return result;
};

const isActivityDisabled = (activity: any) => {
  try {
    const isActive =
      !isNaN(activity?.activityGroupCount as number) &&
      (activity?.activityGroupCount as number) > 0;
    const isPastDate =
      activity.id && activity.date && new Date(activity.date).getTime() < new Date().getTime();

    return isActive || isPastDate;
  } catch (err) {
    return false;
  }
};

const getCustomActivity = () => ({
  id: undefined,
  title: '',
  date: new Date(),
  text: ''
});

const getInitialState = ({
  myChoiceKeyboarding,
  hearItTypeIt,
  teacherCreated,
  activities = []
}: any) => ({
  myChoiceKeyboarding,
  hearItTypeIt,
  teacherCreated,
  activities
});

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  date: Yup.string().required('Date is required'),
  text: Yup.string().required('Text is required')
});

const Enrichment: FC<EnrichmentProps> = ({
  classroom,
  expanded,
  handleChangeExpanded,
  myChoiceKeyboarding,
  hearItTypeIt,
  teacherCreated
}) => {
  const [, { openAlert }] = useStore();
  const { t } = useTranslation('EnrichmentSetting');
  const classes = useStyles();
  const classroomId = +classroom.id;
  const [updateKeyClassroomSettingAssessments] = useMutation(
    UPDATE_KEY_CLASSROOM_ENRICHMENT_SETTING_MUTATION,
    getClassroomKeySettingsMutationCacheUpdate(+classroomId)
  );
  const [state, dispatch] = useReducer<Reducer<EnrichmentState, Action>>(
    enrichmentReducer,
    getInitialState({
      myChoiceKeyboarding: myChoiceKeyboarding?.enabled ?? false,
      hearItTypeIt: hearItTypeIt?.enabled ?? false,
      teacherCreated: teacherCreated?.enabled ?? false
    })
  );

  const [{ form: activityMap, errors, isValid }, { onFormMapChange, setForm, onBlur }] = useForm<{
    [key: string]: Omit<KeyTeacherCreatedActivity, 'classroomId' | '__typename'>;
  }>({
    validationSchema,
    validateOnBlur: true,
    keyBy: 'id',
    initialFormValue: teacherCreated?.activities?.length
      ? keyBy(teacherCreated.activities, 'id')
      : {}
  });

  const refreshState = (data: EnrichmentState | undefined = undefined): void => {
    const payload =
      data ??
      getInitialState({
        myChoiceKeyboarding: myChoiceKeyboarding?.enabled ?? false,
        hearItTypeIt: hearItTypeIt?.enabled ?? false,
        teacherCreated: teacherCreated?.enabled ?? false,
        activities: teacherCreated.activities
      });

    dispatch({
      type: 'REFRESH_STATE',
      payload
    });
  };

  const handleOnCancel = (): void => {
    refreshState();
  };

  const handleToggleMyChoice = (): void =>
    dispatch({
      type: 'SET_ENABLE_MY_CHOICE_KEYBOARDING',
      payload: !state.myChoiceKeyboarding
    });

  const handleToggleHearItTypeIt = (): void =>
    dispatch({ type: 'SET_ENABLE_HEAR_IT_TYPE_IT', payload: !state.hearItTypeIt });

  const handleToggleCustomActivity = (): void =>
    dispatch({
      type: 'SET_ENABLE_CUSTOM_ACTIVITY',
      payload: !state.teacherCreated
    });

  const handleCreateActivity = useCallback(() => {
    setForm({ ...activityMap, [`custom:${Date.now()}`]: getCustomActivity() as any });
  }, [activityMap, setForm]);

  const handleRemoveActivity = useCallback(
    (id: string) => (): void => {
      delete activityMap[id];
      setForm(activityMap);
    },
    [activityMap, setForm]
  );

  const handleOnConfirm = async (): Promise<void> => {
    try {
      const activities = buildActivities(activityMap);
      const {
        data: { updateKeyClassroomSettingEnrichmentActivities: result }
      } = await updateKeyClassroomSettingAssessments({
        variables: {
          data: {
            classroomId,
            hearItTypeIt: {
              enabled: state.hearItTypeIt
            },
            myChoiceKeyboarding: {
              enabled: state.myChoiceKeyboarding
            },
            teacherCreated: {
              enabled: state.teacherCreated,
              activities
            }
          }
        }
      });

      refreshState(
        getInitialState({
          myChoiceKeyboarding: result?.myChoiceKeyboarding?.enabled ?? false,
          hearItTypeIt: result?.hearItTypeIt?.enabled ?? false,
          teacherCreated: result?.teacherCreated?.enabled ?? false,
          activities: result?.teacherCreated.activities
        } as EnrichmentState)
      );
      setForm(keyBy(result?.teacherCreated.activities, 'id'));

      openAlert(getSettingSuccessAlert('Enrichment', t));
    } catch (err) {
      console.log('err: ', err);
    }
  };

  const handleDateChange = (id: string) => (date: Date | null): void => {
    onFormMapChange(id)({ target: { name: 'date', value: date } } as any);
  };

  const activities = Object.entries(activityMap);

  return (
    <SettingsCard
      disabled={!isValid}
      title={t('Enrichment Activities')}
      panel="enrichment"
      expanded={expanded}
      handleChangeExpanded={handleChangeExpanded}
      description={t('Create, schedule or enable activities for additional keyboarding practice.')}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <Fragment>
        <Typography component="div">
          <FormControl component="fieldset" className={classes.container}>
            <FormLabel component="label" className={classes.label}>
              My Choice Keyboarding
              <Typography variant="caption" component="p">
                {t(
                  'Allow students to type free-form answers to simple questions. The activity will appear after the Spot Checks.'
                )}
              </Typography>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={state.myChoiceKeyboarding}
                  onChange={handleToggleMyChoice}
                  name="myChoiceKeyboarding"
                  color="primary"
                />
              }
              label={state.myChoiceKeyboarding ? t('Enabled') : t('Disabled')}
            />
          </FormControl>
        </Typography>
        <Typography component="div">
          <FormControl component="fieldset" className={classes.container}>
            <FormLabel component="label" className={classes.label}>
              Hear it - Type it
              <Typography variant="caption" component="p">
                {t(
                  'Voice Activities for your students. The activity will appear after the Spot Checks'
                )}
              </Typography>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={state.hearItTypeIt}
                  onChange={handleToggleHearItTypeIt}
                  name="hearItTypeIt"
                  color="primary"
                />
              }
              label={state.hearItTypeIt ? t('Enabled') : t('Disabled')}
            />
          </FormControl>
        </Typography>
        <Typography component="div">
          <FormControl component="fieldset" className={classes.container}>
            <FormLabel component="label" className={classes.label}>
              Teacher Custom Creation
              <Typography variant="caption" component="p">
                {t("Add your own activities into the student's curriculum in this class")}
              </Typography>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={state.teacherCreated}
                  onChange={handleToggleCustomActivity}
                  name="teacherCustomCreation"
                  color="primary"
                />
              }
              label={state.teacherCreated ? t('Enabled') : t('Disabled')}
            />
          </FormControl>
        </Typography>
        <Typography component="div" className={classes.activityHolder}>
          <Typography variant="caption" component="p">
            <InfoIcon />
            &nbsp;
            {t('You can schedule up to 10 Teacher Custom Creation activities')}
          </Typography>
          {state.teacherCreated && (
            <Typography component="div" className={classes.activityHolder}>
              {activities.map(([id, activity]) => (
                <CustomEnrichmentActivity
                  id={id}
                  key={id}
                  date={activity.date}
                  text={activity.text}
                  title={activity.title}
                  errors={errors?.[id]}
                  onChange={onFormMapChange(id)}
                  onDateChange={handleDateChange(id)}
                  handleDeleteActivity={handleRemoveActivity}
                  onBlur={onBlur}
                  activityDisabled={isActivityDisabled(activity)}
                />
              ))}
              {activities.length < 10 && (
                <Typography component="p">
                  <Button
                    variant="text"
                    size="small"
                    color="primary"
                    disableElevation
                    onClick={handleCreateActivity}
                    startIcon={<AddCircleIcon />}
                  >
                    {t('Add Another Teacher Creation Activity')}
                  </Button>
                </Typography>
              )}
            </Typography>
          )}
        </Typography>
      </Fragment>
    </SettingsCard>
  );
};

export default Enrichment;
